/**
 * @file animation.js
 * @description Motion 라이브러리(motion.dev) 기반 사내 공용 인터랙션 라이브러리
 *
 * [ Motion.dev 주요 옵션 레퍼런스 ]
 * ---------------------------------------------------------------------------------
 * 1. 기본 트랜지션 (General)
 *    - duration {number}: 애니메이션 지속 시간 (초 단위, 기본값: 0.3)
 *    - delay {number}: 시작 전 대기 시간 (초 단위)
 *    - ease {string|Array}: 이징 함수 ('easeIn', 'easeOut', 'easeInOut', 'backOut', 'circIn' 등)
 *    - repeat {number}: 반복 횟수 (무한 반복은 Infinity)
 *    - repeatType {string}: 반복 형태 ('loop', 'reverse', 'mirror')
 *    - type {string}: 애니메이션 물리 엔진 설정 ('spring', 'tween', 'inertia')
 *    - bounce {number}: 탄성 정도 (0 ~ 1, spring 타입일 때 유효)
 *    - times {number[]}: 키프레임 지점 설정 (0~1 사이 배열, 예: [0, 0.2, 0.8, 1])
 */

window._pa = window._pa || {};
window._pa.anim = (() => {
  const Motion = window.Motion;
  const utils = window._pa;

  if (!Motion) {
    console.error('Motion library not found. Please check your script imports.');
    return {};
  }

  const { animate, stagger, inView, scroll, hover } = Motion;

  // 상수 정의
  const DEFAULT_DURATION = 0.3;
  const DEFAULT_EASE = 'easeOut';
  const DEFAULT_DISTANCE = 20;
  const DEFAULT_SCALE = 0.9;

  /**
   * @description [내부 유틸] 에러 방지용 더미 컨트롤 객체 (Null Object Pattern)
   *
   * 이 객체가 필요한 이유:
   * 1. 런타임 에러 방지: 대상 요소가 존재하지 않을 때 undefined를 리턴하면 .finished.then() 호출 시 스크립트가 죽습니다.
   * 2. 체이닝(Chaining) 보장: 요소 존재 여부와 상관없이 항상 동일한 컨트롤 객체를 반환하여 .play(), .stop() 등을 안전하게 호출할 수 있습니다.
   * 3. 비동기 일관성: 애니메이션이 없는 함수(show, hide)도 finished 프로미스를 반환하게 하여, 호출부에서 await 문법을 일관되게 사용할 수 있습니다.
   *
   * @returns {Object} Motion 컨트롤과 동일한 구조를 가진 가짜(Fake) 컨트롤 객체
   */
  const createDummyControl = () => ({
    play: () => {},
    pause: () => {},
    stop: () => {},
    cancel: () => {},
    complete: () => {},
    finished: Promise.resolve().catch(() => {})
  });

  /**
   * @description [내부 유틸] 등장 애니메이션 전 display 속성을 지능적으로 처리
   *
   * 원리:
   * 1. 인라인 display 스타일을 먼저 제거하여 원래 CSS(Class)에 정의된 값을 확인합니다.
   * 2. 확인된 결과가 여전히 'none'이거나 사용자가 명시적으로 display 값을 준 경우에만 인라인 스타일을 입힙니다.
   * 3. 이를 통해 flex, grid 등 원래의 레이아웃 스타일이 깨지지 않도록 보호합니다.
   *
   * @param {Element[]} $targetElements 처리 대상 요소 배열
   * @param {string|{display?: string}} [displayOrOptions] 표시 방식. 문자열('block'|'flex' 등) 또는 { display }
   * @returns {void}
   */
  const applyDisplay = ($targets, displayOrOptions) => {
    // 1. 명시적 옵션 파악 (루프 밖에서 1회 수행)
    const display = 'string' === typeof displayOrOptions ? displayOrOptions : displayOrOptions && displayOrOptions.display;

    $targets.forEach(($item) => {
      // 2. 이미 캐싱된 값이 있다면 그것을 사용 (Reflow 방지)
      let targetDisplay = display || $item.dataset.paOriginalDisplay;

      // 3. 캐싱된 값이 없는 최초 실행 시에만 측정 수행
      if (!targetDisplay) {
        // 인라인 display를 잠시 제거하여 CSS 클래스에 정의된 값을 확인
        $item.style.removeProperty('display');
        const computedDisplay = window.getComputedStyle($item).display;
        // CSS에서도 none이면 block을 기본값으로, 아니면 CSS 정의값(flex, grid 등) 유지
        targetDisplay = 'none' === computedDisplay ? 'block' : computedDisplay;
        // 4. 측정 결과 캐싱 (이후 호출부터는 getComputedStyle 안 함)
        $item.dataset.paOriginalDisplay = targetDisplay;
      }

      // 5. 최종 결정된 targetDisplay 적용
      // 현재 스타일과 다를 때만 쓰기(Write)를 수행하여 불필요한 레이아웃 계산 방지
      if ($item.style.display !== targetDisplay) {
        $item.style.display = targetDisplay;
      }
    });
  };

  /**
   * @description [내부 유틸] 애니메이션 전 성능 최적화 힌트 적용
   * @param {Element[]} $targetElements - 대상 요소 배열
   */
  const preparePerformance = ($targetElements) => {
    $targetElements.forEach(($item) => {
      $item.style.willChange = 'opacity, transform';
    });
  };

  /**
   * @description [내부 유틸] 애니메이션 후 성능 최적화 힌트 제거
   * @param {Element[]} $targetElements - 대상 요소 배열
   */
  const cleanupPerformance = ($targetElements) => {
    $targetElements.forEach(($item) => {
      $item.style.removeProperty('will-change');
    });
  };

  /**
   * @description [내부 유틸] 여러 애니메이션 컨트롤을 하나로 묶음
   *
   * 프로미스 에러 핸들링(.catch):
   * Motion 애니메이션이 중간에 다른 애니메이션에 의해 중단되거나 강제 종료되면 finished 프로미스가
   * Reject됩니다. 이때 .catch 처리가 없으면 콘솔에 Uncaught 에러가 발생하므로 내부적으로 삼킵니다.
   *
   * @param {Object[]} $controlsList - 컨트롤 객체 배열
   * @returns {Object} 통합 컨트롤 객체
   */
  const combineControls = ($controlsList) => {
    // 1. 모든 프로미스가 완료(성공 또는 중단)될 때까지 대기
    // Promise.allSettled를 사용하여 하나가 중단되어도 전체 흐름을 유지함
    const combinedFinished = Promise.allSettled($controlsList.map((control) => control.finished));

    return {
      play: () => $controlsList.forEach((control) => control.play?.()),
      pause: () => $controlsList.forEach((control) => control.pause?.()),
      stop: () => $controlsList.forEach((control) => control.stop?.()),
      cancel: () => $controlsList.forEach((control) => control.cancel?.()),
      finished: combinedFinished
    };
  };

  return {
    // Motion.dev 원본 API
    animate,
    stagger,
    inView,
    scroll,
    hover,

    /**
     * @description show(): 요소를 단순히 나타나게 함 (애니메이션 없이 display 설정)
     * @param {string|Element|NodeList|Array<Element>} $element - 대상 요소(선택자/엘리먼트/컬렉션)
     * @param {string|Object} [displayOrOptions] - 'block'|'flex'|'grid' 또는 { display: '...' }
     * @returns {Object} 더미 컨트롤
     */
    show: ($element, displayOrOptions) => {
      const $targetElements = utils.normalizeElements($element);
      applyDisplay($targetElements, displayOrOptions);
      return createDummyControl();
    },

    /**
     * @description hide(): 요소를 단순히 숨김 (애니메이션 없이 display: none)
     * @param {string|Element} $element - 대상 요소
     * @returns {Object} 더미 컨트롤
     */
    hide: ($element) => {
      const $targetElements = utils.normalizeElements($element);
      $targetElements.forEach(($item) => {
        $item.style.display = 'none';
      });
      return createDummyControl();
    },

    /**
     * @description toggle(): 현재 가시 상태에 따라 show/hide 전환
     * @param {string|Element} $element - 대상 요소
     * @param {string|Object} [displayOrOptions] - show 시 적용할 옵션 - 'block'|'flex'|'grid' 또는 { display: '...' }
     * @returns {Object} 더미 컨트롤
     */
    toggle: function ($element, displayOrOptions) {
      const $targetElements = utils.normalizeElements($element);
      $targetElements.forEach(($item) => (utils.isVisible($item) ? this.hide($item) : this.show($item, displayOrOptions)));
      return createDummyControl();
    },

    /**
     * @description fadeIn(): 투명도 애니메이션으로 요소를 서서히 나타나게 함
     * @param {string|Element} $element - 대상 요소
     * @param {Object} [options] - Motion 애니메이션 옵션
     * @returns {Object} 애니메이션 컨트롤
     */
    fadeIn: ($element, options = {}) => {
      const $targetElements = utils.normalizeElements($element);
      if (0 === $targetElements.length) return createDummyControl();

      applyDisplay($targetElements, options);
      preparePerformance($targetElements);

      const animationControl = animate($targetElements, { opacity: [0, 1] }, { duration: DEFAULT_DURATION, ...options });
      animationControl.finished.finally(() => cleanupPerformance($targetElements)).catch(() => {});
      return animationControl;
    },

    /**
     * @description fadeOut(): 투명도 애니메이션 후 요소를 숨김 (display: none)
     * @param {string|Element} $element - 대상 요소
     * @param {Object} [options] - Motion 애니메이션 옵션
     * @returns {Object} 애니메이션 컨트롤
     */
    fadeOut: ($element, options = {}) => {
      const $targetElements = utils.normalizeElements($element);
      if (0 === $targetElements.length) return createDummyControl();

      preparePerformance($targetElements);
      const animationControl = animate($targetElements, { opacity: 0 }, { duration: DEFAULT_DURATION, ...options });
      animationControl.finished
        .then(() => {
          $targetElements.forEach(($item) => ($item.style.display = 'none'));
        })
        .finally(() => cleanupPerformance($targetElements))
        .catch(() => {});

      return animationControl;
    },

    /**
     * @description fadeToggle(): 가시 상태에 따라 fadeIn/fadeOut 전환
     * @param {string|Element} $element - 대상 요소
     * @param {Object} [options] - 애니메이션 옵션
     * @returns {Object} 통합 컨트롤
     */
    fadeToggle: function ($element, options = {}) {
      const $targetElements = utils.normalizeElements($element);
      const $controlsList = $targetElements.map(($item) => (utils.isVisible($item) ? this.fadeOut($item, options) : this.fadeIn($item, options)));
      return combineControls($controlsList);
    },

    /**
     * @description slideDown(): 요소의 높이를 0에서 컨텐츠 높이까지 애니메이션 (Opacity 제외)
     * @param {string|Element} $element - 대상 요소
     * @param {Object} [options] - 애니메이션 옵션
     * @returns {Object} 통합 컨트롤
     */
    slideDown: ($element, options = {}) => {
      const $targetElements = utils.normalizeElements($element);
      if (0 === $targetElements.length) return createDummyControl();

      applyDisplay($targetElements, options);
      const $controlsList = $targetElements.map(($item) => {
        $item.style.overflow = 'hidden';
        $item.style.height = '0px';

        const animationControl = animate($item, { height: [0, $item.scrollHeight] }, { duration: DEFAULT_DURATION, ease: DEFAULT_EASE, ...options });
        animationControl.finished
          .then(() => {
            $item.style.removeProperty('height');
            $item.style.removeProperty('overflow');
          })
          .catch(() => {});
        return animationControl;
      });
      return combineControls($controlsList);
    },

    /**
     * @description slideUp(): 요소의 높이를 0까지 줄인 후 숨김 (Opacity 제외)
     * @param {string|Element} $element - 대상 요소
     * @param {Object} [options] - 애니메이션 옵션
     * @returns {Object} 통합 컨트롤
     */
    slideUp: ($element, options = {}) => {
      const $targetElements = utils.normalizeElements($element);
      if (0 === $targetElements.length) return createDummyControl();

      const $controlsList = $targetElements.map(($item) => {
        const currentHeight = $item.offsetHeight;
        $item.style.overflow = 'hidden';
        const animationControl = animate($item, { height: [currentHeight, 0] }, { duration: DEFAULT_DURATION, ease: DEFAULT_EASE, ...options });
        animationControl.finished
          .then(() => {
            $item.style.display = 'none';
            $item.style.removeProperty('height');
            $item.style.removeProperty('overflow');
          })
          .catch(() => {});
        return animationControl;
      });
      return combineControls($controlsList);
    },

    /**
     * @description slideToggle(): 가시 상태에 따라 slideUp/slideDown 전환
     * @param {string|Element} $element - 대상 요소
     * @param {Object} [options] - 애니메이션 옵션
     * @returns {Object} 통합 컨트롤
     */
    slideToggle: function ($element, options = {}) {
      const $targetElements = utils.normalizeElements($element);
      const $controlsList = $targetElements.map(($item) => (utils.isVisible($item) ? this.slideUp($item, options) : this.slideDown($item, options)));
      return combineControls($controlsList);
    },

    /**
     * @description slideIn: 특정 방향에서 밀려 들어오며 등장 (Opacity 포함)
     * @param {string|Element} $element - 대상 요소
     * @param {Object} config - { from: 'top'|'bottom'|'left'|'right', distance: 숫자 }
     * @param {Object} [options] - Motion 애니메이션 옵션
     * @returns {Object} 애니메이션 컨트롤
     */
    slideIn: ($element, { from = 'bottom', distance = DEFAULT_DISTANCE, ...options } = {}) => {
      const $targetElements = utils.normalizeElements($element);
      if (0 === $targetElements.length) return createDummyControl();

      applyDisplay($targetElements, options);
      preparePerformance($targetElements);

      const axis = 'left' === from || 'right' === from ? 'x' : 'y';
      const startValue = 'left' === from || 'top' === from ? -distance : distance;
      const animationControl = animate($targetElements, { [axis]: [startValue, 0], opacity: [0, 1] }, { duration: DEFAULT_DURATION, ...options });
      animationControl.finished.finally(() => cleanupPerformance($targetElements)).catch(() => {});

      return animationControl;
    },

    /**
     * @description slideOut: 특정 방향으로 밀려 나가며 퇴장 (Opacity 포함)
     * @param {string|Element} $element - 대상 요소
     * @param {Object} config - { to: 'top'|'bottom'|'left'|'right', distance: 숫자 }
     * @param {Object} [options] - Motion 애니메이션 옵션
     * @returns {Object} 애니메이션 컨트롤
     */
    slideOut: ($element, { to = 'bottom', distance = DEFAULT_DISTANCE, ...options } = {}) => {
      const $targetElements = utils.normalizeElements($element);
      if (0 === $targetElements.length) return createDummyControl();

      preparePerformance($targetElements);
      const axis = 'left' === to || 'right' === to ? 'x' : 'y';
      const endValue = 'left' === to || 'top' === to ? -distance : distance;
      const animationControl = animate($targetElements, { [axis]: endValue, opacity: 0 }, { duration: DEFAULT_DURATION, ...options });
      animationControl.finished
        .then(() => {
          $targetElements.forEach(($item) => ($item.style.display = 'none'));
        })
        .finally(() => cleanupPerformance($targetElements))
        .catch(() => {});

      return animationControl;
    },

    /**
     * @description scaleIn: 커지며 강조 효과와 함께 등장
     * @param {string|Element} $element - 대상 요소
     * @param {Object} config - { from: 시작 스케일값 }
     * @param {Object} [options] - Motion 애니메이션 옵션
     * @returns {Object} 애니메이션 컨트롤
     */
    scaleIn: ($element, { from = DEFAULT_SCALE, ...options } = {}) => {
      const $targetElements = utils.normalizeElements($element);
      if (0 === $targetElements.length) return createDummyControl();

      applyDisplay($targetElements, options);
      preparePerformance($targetElements);

      const animationControl = animate($targetElements, { scale: [from, 1], opacity: [0, 1] }, { duration: DEFAULT_DURATION, ...options });
      animationControl.finished.finally(() => cleanupPerformance($targetElements)).catch(() => {});

      return animationControl;
    },

    /**
     * @description scaleOut: 작아지며 부드럽게 퇴장
     * @param {string|Element} $element - 대상 요소
     * @param {Object} config - { to: 최종 스케일값 }
     * @param {Object} [options] - Motion 애니메이션 옵션
     * @returns {Object} 애니메이션 컨트롤
     */
    scaleOut: ($element, { to = DEFAULT_SCALE, ...options } = {}) => {
      const $targetElements = utils.normalizeElements($element);
      if (0 === $targetElements.length) return createDummyControl();

      preparePerformance($targetElements);
      const animationControl = animate($targetElements, { scale: to, opacity: 0 }, { duration: DEFAULT_DURATION, ...options });
      animationControl.finished
        .then(() => {
          $targetElements.forEach(($item) => ($item.style.display = 'none'));
        })
        .finally(() => cleanupPerformance($targetElements))
        .catch(() => {});

      return animationControl;
    }
  };
})();
